/**
 * clickable-card-fitment.js — paints fitment chips on product cards.
 *
 * Reads vehicle state once on load and subscribes to
 * 'clickable:vehicle-changed' (never re-reads storage on events — the event
 * detail is the source). Match = case-insensitive full-string equality of the
 * active vehicle name against each card's model array; FIRST MATCH WINS
 * (documented convention — duplicate vehicle rows carry duplicate warnings).
 *
 * Painted states (kit §11 / §22 / §40 + FID-03 round-two re-transplant):
 * - match, no warning  → green "✓ Fits your [car]" pill + green tick icon
 * - match with warning → canon amber LINE (⚠ + the raw condition note,
 *                        ProductCard.jsx WarningZone:229-238 — the grey
 *                        matched-with-condition box is RETIRED on cards;
 *                        the "Check before ordering:" prefix stays a PDP
 *                        element) + amber triangle icon
 * - helpful message    → canon teal dot line (:220-227)
 * - no vehicle / no match → zone stays empty (reserved space, no CLS)
 *
 * PERSISTENT ADDED (round-two item 3): any card whose variant is in the
 * cart renders the Added state on load (cart.js) and after cart mutations,
 * persisting until removed from the cart; the button stays live for repeat
 * adds. Canon has no in-cart state (PriceCTA's Added is a 1.8s flash) —
 * built ADDED language carries it, flagged.
 *
 * All message content is inserted via textContent — metafield text is data,
 * never markup.
 */
(function () {
  'use strict';

  /* RUN-ONCE GUARD (perf round item 5, Lucas ruling 21 Aug — rule 21
     corollary): this module ships from three sections (collection grid,
     home rows, search results); classic scripts execute PER TAG, and a
     second execution would double-register the document click delegate
     and the grid MutationObserver. home-rows.liquid's comment always
     claimed this guard existed — now it does. */
  if (window.__ckCardFitmentLoaded) return;
  window.__ckCardFitmentLoaded = true;

  var activeName = null;

  /* LOCKSTEP DELEGATION (21 Aug): the row-match comparison lives ONLY in
     clickable-fit-predicate.js (sync head, fourth lockstep member) —
     these are thin delegates, fail-closed when the module is absent. */
  function firstMatchIndex(models, name) {
    return window.clickableFitPredicate
      ? window.clickableFitPredicate.firstMatchIndex(models, name) : -1;
  }

  function entryAt(list, index) {
    return window.clickableFitPredicate
      ? window.clickableFitPredicate.entryAt(list, index) : '';
  }

  // Condensed display name for FIT SURFACES (Lucas, 29 Jul — the round-two
  // display rule applies sitewide: banners read "Fits your BMW F30/F31
  // 330i", never the full canonical string; the H1 stays the precision
  // surface). PRESENTATION ONLY — matching and cart properties keep the
  // full canonical name. Owner is clickable-fitment.js shortName (PDP-only
  // script); this MIRROR follows the clickable-apps precedent and is
  // cross-referenced there — October centralises.
  function displayVehicleName(name) {
    if (!name) return name;
    if (window.clickableShortVehicleName) return window.clickableShortVehicleName(name);
    var out = name.replace(/\s*\([^)]*\)/g, '');
    out = out.replace(/^BMW \d Series /, 'BMW ');
    return out.replace(/\s+/g, ' ').trim();
  }

  // Quick-add button states (kit §23) — driven by the same first-match the
  // chips use. State 1 (no vehicle): outlined, tooltip intercepts the click.
  // State 4/5 (match): orange fill, metafield-driven "Add {n} for {total}"
  // label. No match: button removed, neutral "Not listed for your [car]"
  // fills the same fixed-height zone (copy flagged — see §43 trigger flag).
  /* b152: symbol comes from the card's own data-currency-symbol (emitted
     beside data-price-cents; page-constant — one presentment per request).
     ZERO-DECIMAL CAVEAT (noted, not solved): /100 assumes two-decimal
     money; JPY-class currencies would mis-scale here. */
  var ckCurSym = null;
  function money(cents) {
    if (ckCurSym === null) {
      var src = document.querySelector('[data-currency-symbol]');
      ckCurSym = (src && src.getAttribute('data-currency-symbol')) || '$';
    }
    var dollars = cents / 100;
    var whole = dollars % 1 === 0;
    return ckCurSym + dollars.toLocaleString('en-AU', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  }

  // Card fit verdict — delegates to THE choke point (FITMENT-HARDENING-01):
  // clickableVehicleState.fitVerdict asserts full-model state at entry, so a
  // partial context (bare make) can never produce a card tick or a
  // quick-add. Returns the module's {verdict, name, tag} shape.
  function cardFitVerdict(card) {
    if (!window.clickableVehicleState) return { verdict: 'unset', name: null, tag: null };
    var tags = [];
    var raw = card.getAttribute('data-vtags');
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) tags = parsed;
      } catch (error) {
        /* unreadable tag blob — no tags, verdict falls to red-under-vehicle */
      }
    }
    return window.clickableVehicleState.fitVerdict(tags);
  }

  // Variants currently in the cart (persistent Added state). Refreshed on
  // load, after quick-adds, on toast-shown (PDP adds land there too) and on
  // Dawn's cartUpdate pub-sub when present (drawer removals).
  var cartVariants = null;

  function refreshCartState() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartVariants = {};
        (cart.items || []).forEach(function (item) {
          cartVariants[String(item.variant_id)] = (cartVariants[String(item.variant_id)] || 0) + item.quantity;
        });
      })
      .catch(function () { /* cart unknown — states paint without Added */ });
  }

  function syncAdded(card) {
    /* "Added" chip (Lucas build, 19 Aug): the phone card's in-cart
       signal (<=989 — the ATC is hidden <=767). Same truth as the
       button, synced BEFORE the button guard so OOS cards (which render
       no ATC) stay live too. */
    var chip = card.querySelector('[data-card-added-chip]');
    if (chip && cartVariants) {
      if ((cartVariants[card.getAttribute('data-variant-id')] || 0) > 0) {
        chip.removeAttribute('hidden');
      } else {
        chip.setAttribute('hidden', '');
      }
    }
    var button = card.querySelector('[data-card-atc]');
    if (!button || !cartVariants) return;
    var inCart = (cartVariants[card.getAttribute('data-variant-id')] || 0) > 0; /* map now holds QUANTITIES (11 Aug clamp round) */
    if (inCart) {
      if (!button.hasAttribute('data-base-label')) {
        button.setAttribute('data-base-label', button.textContent);
      }
      button.classList.add('is-added');
      button.textContent = 'Added';
    } else if (button.classList.contains('is-added')) {
      button.classList.remove('is-added');
      if (button.hasAttribute('data-base-label')) {
        button.textContent = button.getAttribute('data-base-label');
        button.removeAttribute('data-base-label');
      }
    }
    /* LIVE-STATE stock warning (11 Aug round 1): syncAdded runs on every
       cart resync — clear the marked warning once this variant's cart
       quantity is back under max. Warnings without the marker (fitment
       copy) are never touched. */
    var warn = card.querySelector('[data-card-nofit][data-ck-stock-warn]');
    if (warn) {
      var max = parseInt(card.getAttribute('data-ck-max'), 10);
      var q = cartVariants[card.getAttribute('data-variant-id')] || 0;
      if (isNaN(max) || q < max) {
        warn.setAttribute('hidden', '');
        warn.textContent = '';
        warn.removeAttribute('data-ck-stock-warn');
      }
    }
  }

  function paintQuickAdd(card, matched) {
    var button = card.querySelector('[data-card-atc]');
    var nofit = card.querySelector('[data-card-nofit]');
    if (!button) return;

    var qty = Math.max(1, parseInt(card.getAttribute('data-default-qty'), 10) || 1);
    var cents = parseInt(card.getAttribute('data-price-cents'), 10) || 0;
    var isGeneric = card.hasAttribute('data-generic');
    var fits = matched; // caller passes the tag verdict (canon: tags decide)

    if (nofit) {
      nofit.setAttribute('hidden', '');
      nofit.textContent = '';
      nofit.removeAttribute('data-ck-stock-warn');
    }
    button.removeAttribute('hidden');
    button.removeAttribute('data-qf-change');
    button.classList.remove('clickable-card__atc--ready', 'is-added');
    button.removeAttribute('data-base-label');
    card.classList.remove('clickable-card--no-vehicle');

    // Idempotent writes only — this paints inside a MutationObserver's
    // subtree; unconditional textContent writes would re-trigger it.
    function setText(el, text) {
      if (el.textContent !== text) el.textContent = text;
    }

    if (!activeName && isGeneric) {
      // GENERIC SIMPLIFICATION (11 Aug): the tag's only effect is the
      // no-vehicle state — plain add, no gate, no fitment claims. With a
      // vehicle set, tags decide below like any product.
      setText(button, qty > 1 ? 'Add ' + qty + ' for ' + money(cents * qty) : 'Add to cart');
    } else if (!activeName) {
      // ATC surface-dependence (Lucas, 26 Jul — supersedes 04c here): on
      // CARDS the no-vehicle state is BLOCKED — hover/tap explains, click
      // routes to the QuickFinder. FIDELITY-03 + 02x ruling: the route is
      // the click-through POPUP — data-qf-change hands the click to the
      // selector's capture-phase opener; if chooser data is unavailable the
      // click falls through to the scroll fallback below. No dead-end
      // disabled button, no layout shift. (PDP keeps soft amber + working
      // ATC.)
      card.classList.add('clickable-card--no-vehicle');
      button.setAttribute('data-qf-change', '');
      setText(button, 'Add to cart');
    } else if (fits) {
      button.classList.add('clickable-card__atc--ready');
      setText(button, qty > 1 ? 'Add ' + qty + ' for ' + money(cents * qty) : 'Add to cart');
    } else if (nofit) {
      // State 6 (kit §23) with §43 policy: no silent quick-add — route to
      // the PDP where the acknowledgement gate lives.
      button.setAttribute('hidden', '');
      nofit.textContent = '';
      var strong = document.createElement('strong');
      strong.textContent = "Doesn't fit your " + displayVehicleName(activeName) + '. ';
      nofit.appendChild(strong);
      var link = document.createElement('a');
      link.href = card.getAttribute('data-product-url') || '#';
      link.textContent = 'View details';
      nofit.appendChild(link);
      nofit.removeAttribute('hidden');
    }

    // Persistent Added overlay (in-cart variants) rides on the painted state.
    syncAdded(card);
  }

  function quickAdd(card) {
    var button = card.querySelector('[data-card-atc]');
    var variantId = card.getAttribute('data-variant-id');
    if (!button || !variantId) return;

    var qty = Math.max(1, parseInt(card.getAttribute('data-default-qty'), 10) || 1);
    var isGeneric = card.hasAttribute('data-generic');
    var properties = {};

    // Canon: tags decide — via the choke point ONLY (generic included,
    // 11 Aug simplification: with a vehicle set the tag match writes the
    // same verified properties as any product). Generic-without-vehicle
    // adds plain (no claims). Mismatch never quick-adds — the PDP red
    // state owns it.
    var fv = cardFitVerdict(card);
    if (fv.verdict === 'unset' && isGeneric) {
      // no vehicle: clean add, no fitment claims
    } else {
      if (fv.verdict !== 'fit') return;
      properties.Car = fv.name;
      var raw = card.getAttribute('data-fitment');
      if (raw) {
        try {
          var fitment = JSON.parse(raw);
          var index = firstMatchIndex(fitment.model, fv.name);
          if (index !== -1) {
            var warning = entryAt(fitment.warning, index);
            if (warning) properties.Warning = warning;
          }
        } catch (error) {
          /* no accentuate detail — Car alone follows */
        }
      }
    }

    var originalLabel = button.textContent;
    /* OPTIMISTIC CLAMP (the client's picture; the server's answer still
       wins via the catch): repeated quick-adds past stock clamp the
       REQUEST to the remainder and warn; at-stock adds nothing and warns. */
    var ckMax = parseInt(card.getAttribute('data-ck-max'), 10);
    if (!isNaN(ckMax)) {
      var inCartQty = cartVariants[String(variantId)] || 0;
      if (inCartQty >= ckMax) {
        cardStockWarn(card);
        return;
      }
      if (inCartQty + qty > ckMax) {
        qty = ckMax - inCartQty;
        cardStockWarn(card);
      }
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty, properties: properties }),
    })
      .then(function (response) { return response.json(); })
      .then(function (result) {
        if (result.status) throw new Error(result.description || 'Add failed');
        // Persistent Added (round-two item 3): no revert timer — the state
        // holds while the variant is in the cart; the button stays live.
        if (!button.hasAttribute('data-base-label')) {
          button.setAttribute('data-base-label', originalLabel);
        }
        button.classList.add('is-added');
        button.textContent = 'Added';
        /* chip: same optimistic flip as the button; the /cart.js truth
           sweep below re-confirms it across every card */
        var ownChip = card.querySelector('[data-card-added-chip]');
        if (ownChip) ownChip.removeAttribute('hidden');
        // §28 toast — same feedback surface as the PDP path. The add
        // response carries no product tags, so the GENERIC bit rides
        // along from the card (13 Aug generic-silence exemption).
        if (result && card.hasAttribute('data-generic')) result.__ckGeneric = true;
        if (window.clickableAtcToast) window.clickableAtcToast.show(result);
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        /* COUNTER FIX (11 Aug): .ck-cart-count no longer exists — the
           header badge is the section-rendered #cart-icon-bubble slot.
           The clamp module owns the three cart surfaces' truth swaps. */
        if (window.clickableCartRefresh) window.clickableCartRefresh();
        cartVariants = {};
        (cart.items || []).forEach(function (item) {
          cartVariants[String(item.variant_id)] = (cartVariants[String(item.variant_id)] || 0) + item.quantity;
        });
        document.querySelectorAll('[data-product-card]').forEach(syncAdded);
      })
      .catch(function (error) {
        /* SERVER PATH (pressure matrix): a 422 means the server's stock
           picture beat ours — never raw, never silent. Friendly copy into
           the card's message voice, then truth-resync every surface. */
        button.textContent = originalLabel;
        cardStockWarn(card);
        if (window.clickableCartRefresh) window.clickableCartRefresh();
        syncCartQuantities();
      });
  }

  function cardStockWarn(card) {
    var slot = card.querySelector('[data-card-nofit]');
    var max = parseInt(card.getAttribute('data-ck-max'), 10);
    var msg = window.clickableStockMsg && !isNaN(max)
      ? window.clickableStockMsg(max)
      : 'Quantity not available.';
    if (!slot) return;
    /* LIVE STATE (Lucas, 11 Aug round 1 — supersedes the 4s auto-hide):
       the warning holds while the cart sits at max for this variant and
       clears from syncAdded the moment the quantity drops back under.
       The marker keeps the clear away from fitment copy sharing the
       slot. */
    slot.textContent = msg;
    slot.setAttribute('data-ck-stock-warn', '');
    slot.removeAttribute('hidden');
  }

  function syncCartQuantities() {
    return refreshCartState().then(function () {
      document.querySelectorAll('[data-product-card]').forEach(syncAdded);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-card-atc]');
    if (!button) return;
    var card = button.closest('[data-product-card]');
    if (!card) return;
    // Chooser surfaces never paint states, so their raw buttons must never add.
    if (card.closest('[data-suppress-fitment]')) return;
    // No-vehicle non-generic card: route to the QuickFinder, never add.
    if (card.classList.contains('clickable-card--no-vehicle') && !card.hasAttribute('data-generic')) {
      var finder = document.querySelector('[data-qf-card]');
      if (finder) finder.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    quickAdd(card);
  });

  // Canon FitmentIcon glyphs (ProductCard.jsx:50-86) — fixed markup, never
  // data: green polyline check / filled amber triangle in a white circle.
  var ICON_FITS_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  var ICON_WARN_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M12 2 L22 20 L2 20 Z"></path><rect x="11" y="9" width="2" height="6" fill="#fff"></rect><rect x="11" y="16" width="2" height="2" fill="#fff"></rect></svg>';

  function paintCard(card) {
    var zone = card.querySelector('[data-fitment-zone]');
    var icon = card.querySelector('[data-fitment-icon]');
    if (!zone) return;

    /* SELECTIVE wipe (slot-priority round, 11 Aug): the zone may hold
       the relocated notify/expected-back elements — a textContent=''
       wipe would destroy them on repaint. Only our fitment lines go.
       PERF ROUND item 1: the promptline is now PERMANENT server DOM
       (visibility is pure CSS off the pre-paint stamp) — never wiped.
       OPTION H (Lucas ruling 21 Aug): server latent chips
       ([data-ck-server-chip]) are permanent too — a repaint RE-LATCHES
       them (restores data-ck-latent) instead of removing; only
       JS-created chips are destroyed. */
    zone.querySelectorAll('.clickable-fit-helpful, .clickable-fit-warnline, .clickable-fit-fitline, .clickable-fit-nofitline').forEach(function (el) {
      if (el.hasAttribute('data-ck-server-chip')) {
        el.setAttribute('data-ck-latent', '');
        /* exact-reservation (21 Aug): a stale pre-paint mark must die
           with the state that made it, or the old vehicle's chip stays
           in layout under the new one. */
        el.removeAttribute('data-ck-match');
      }
      else el.remove();
    });
    if (icon) {
      icon.textContent = '';
      icon.setAttribute('hidden', '');
      icon.classList.remove('clickable-card__fit-icon--fits', 'clickable-card__fit-icon--warn');
    }

    // Round-three item 2 (Lucas): card fit BANNERS are out of design scope
    // — the image-corner icon circle IS the fit signal on cards (canon's
    // compact language); pills belong to toast/cart/PDP surfaces only.
    function paintFitIcon() {
      if (icon) {
        icon.innerHTML = ICON_FITS_SVG;
        icon.classList.add('clickable-card__fit-icon--fits');
        icon.removeAttribute('hidden');
      }
    }

    /* R5b item 1 (Lucas): the VERIFIED state is SILENT on cards — no
       icon, no pill, at every width (the round-three "icon is the
       signal" ruling is superseded). Only criteria warnings and the
       mismatch line paint. PDP unchanged. */
    function phoneList() { return window.matchMedia('(max-width: 767px)').matches; }
    function emitNoFitLine() {
      if (!phoneList() || !activeName) return;
      var line = document.createElement('div');
      line.className = 'clickable-fit-nofitline';
      line.textContent = "Doesn't fit your " + displayVehicleName(activeName);
      zone.appendChild(line);
    }

    // CANONICAL CONTRACT (Task 04c §A): tags alone decide fit; accentuate
    // only supplies the warning/helpful text for a tag-confirmed fit. The
    // verdict comes ONLY from the choke point (FITMENT-HARDENING-01):
    // below full-model state it returns 'unset' and the card composes the
    // no-vehicle state — no icon-circle signal, blocked quick-add.
    var fv = cardFitVerdict(card);
    activeName = fv.name;
    if (fv.verdict === 'unset') {
      paintQuickAdd(card, false);
      /* R5e item 1 (SUPERSEDES 5d's own-warning clause): with NO
         vehicle the prompt line is the ONLY paint — criteria warnings
         are vehicle-relative and are noise without one. No warnings, no
         mismatch, no verified until a vehicle is set. Generic-tag cards
         stay silent (the generic rule). <=767 only; PDP untouched. */
      /* PERF ROUND item 1: the promptline is server-rendered inside the
         zone (permanent DOM, CSS-gated by the pre-paint stamp) — ADOPT it
         and refine copy IN PLACE (rule 29); create only as a fallback for
         a card that shipped without one. Width/generic gating is CSS's
         job now; the create fallback keeps the old JS gates. */
      var zoneU = card.querySelector('[data-fitment-zone]');
      if (zoneU) {
        var chrome = document.querySelector('[data-collection-chrome]');
        var copy = (chrome && chrome.getAttribute('data-ck-prompt-copy')) || 'Enter your vehicle to check fitment';
        var pl = zoneU.querySelector('.clickable-fit-promptline');
        if (pl) {
          if (pl.textContent !== copy) pl.textContent = copy;
        } else if (phoneList() && !card.hasAttribute('data-generic')) {
          pl = document.createElement('button');
          pl.type = 'button';
          pl.className = 'clickable-fit-promptline';
          pl.setAttribute('data-qf-change', '');
          pl.textContent = copy;
          zoneU.insertBefore(pl, zoneU.firstChild);
        }
      }
      return;
    }

    var tagFits = fv.verdict === 'fit';
    var fitment = null;
    var raw = card.getAttribute('data-fitment');
    if (raw) {
      try {
        fitment = JSON.parse(raw);
      } catch (error) {
        fitment = null;
      }
    }
    var index = fitment ? firstMatchIndex(fitment.model, activeName) : -1;

    if (tagFits !== (index !== -1)) {
      var handle = (card.getAttribute('data-product-url') || '').split('/').pop();
      console.info('[fitment-drift] ' + handle + ': ' + (tagFits
        ? 'tag-fit without accentuate row'
        : 'accentuate row without tag (red by canon)'));
    }

    paintQuickAdd(card, tagFits);
    if (!tagFits) { emitNoFitLine(); return; }
    if (index === -1) {
      /* LUCAS RULING (18 Aug, supersedes 5b item 1's desktop half):
         desktop cards KEEP the verified tick as designed — verified is
         silent at <=767 ONLY. */
      if (!phoneList()) paintFitIcon();
      return;
    }

    var warning = entryAt(fitment.warning, index);
    var helpful = entryAt(fitment.helpful, index);

    // Canon WarningZone order (round-three item 8, ProductCard.jsx:214-238):
    // helpful (teal) ABOVE, critical (amber) BELOW — the zone bottom-anchors
    // its content, so the critical line sits closest to the price/ATC group.
    /* OPTION H reveal (Lucas ruling 21 Aug): prefer the server latent
       chip with the same text — removing data-ck-latent is an in-place
       visibility flip at identical geometry (zero shift). Creation
       survives only as the data-drift fallback. Date highlighting runs
       once per chip (marker), at reveal — it wraps ink, not boxes. */
    function revealLatent(selector, text) {
      var m = null;
      zone.querySelectorAll(selector + '[data-ck-server-chip]').forEach(function (el) {
        if (m) return;
        var noteEl = el.matches('.clickable-fit-warnline') ? el.children[1] : el;
        if (noteEl && noteEl.textContent.trim() === String(text).trim()) m = { chip: el, note: noteEl };
      });
      if (!m) return false;
      m.chip.removeAttribute('data-ck-latent');
      if (!m.chip.hasAttribute('data-ck-hl') && window.clickableHighlightFitmentDates) {
        window.clickableHighlightFitmentDates(m.note);
        m.chip.setAttribute('data-ck-hl', '');
      }
      return true;
    }

    if (helpful && !revealLatent('.clickable-fit-helpful', helpful)) {
      var helpLine = document.createElement('p');
      helpLine.className = 'clickable-fit-helpful';
      helpLine.textContent = helpful;
      if (window.clickableHighlightFitmentDates) window.clickableHighlightFitmentDates(helpLine);
      zone.appendChild(helpLine);
    }

    if (warning && revealLatent('.clickable-fit-warnline', warning)) {
      if (icon) {
        icon.innerHTML = ICON_WARN_SVG;
        icon.classList.add('clickable-card__fit-icon--warn');
        icon.removeAttribute('hidden');
      }
    } else if (warning) {
      // Canon warned line: ⚠ glyph + the raw condition note (boxes and
      // prefixes retired on cards; PDP keeps its own arbitrated treatment).
      var warnLine = document.createElement('div');
      warnLine.className = 'clickable-fit-warnline';
      var glyph = document.createElement('span');
      /* R5d item 4: the SVG warning icon SITEWIDE — the text character
         renders as an emoji on iOS, so the 5c desktop fork is retired;
         every card warned line carries the icon. 15x15 on the mobile
         pill (scales with the 15px type), 12x12 on the desktop line
         (its 12px type) — rule 19 explicit dims both ways. */
      var gsize = phoneList() ? 14 : 12; /* R5e: pill 14px, icon scales */
      glyph.innerHTML = '<svg width="' + gsize + '" height="' + gsize + '" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M12 2 L22 20 L2 20 Z"></path><rect x="11" y="9" width="2" height="6" fill="#fff"></rect><rect x="11" y="16" width="2" height="2" fill="#fff"></rect></svg>';
      warnLine.appendChild(glyph);
      var note = document.createElement('span');
      note.textContent = warning;
      if (window.clickableHighlightFitmentDates) window.clickableHighlightFitmentDates(note);
      warnLine.appendChild(note);
      zone.appendChild(warnLine);

      if (icon) {
        icon.innerHTML = ICON_WARN_SVG;
        icon.classList.add('clickable-card__fit-icon--warn');
        icon.removeAttribute('hidden');
      }
    } else {
      /* verified tick restored on desktop (Lucas ruling); <=767 silent */
      if (!phoneList()) paintFitIcon();
    }
  }

  /* SLOT PRIORITY (Lucas, 11 Aug corner round item 3): the warning zone
     above the price is a priority slot — fitment warnings ALWAYS own it
     and never move. On an OOS card with NO warning painted, the
     Notify-me link + Expected-back line move DOWN into the zone; the
     moment a warning paints, they return to their reference positions
     (grid: the stacked block under the title; list: inline in the meta
     row). In-stock cards render no notify elements — unchanged by
     construction. Both view variants move together — the view-scoped
     classes keep exactly one visible, so grid and list each stay
     correct wherever the elements sit. */
  function applySlotPriority(card) {
    var zone = card.querySelector('[data-fitment-zone]');
    if (!zone) return;
    /* LIST-VIEW OOS COMPOSITION (Lucas, session 5): the list variant's
       notify + Expected-back lines now live in the ACTION ZONE as a fixed
       stack under the OUT OF STOCK notice — they are NOT movables any more
       (moving them was exactly the floating the ruling removes). Only the
       GRID variant (the stacked block under the title) still arbitrates
       for the warning slot, so grid behaviour is untouched. */
    var movables = [];
    var oosblock = card.querySelector('.clickable-card__oosblock');
    if (oosblock) movables.push(oosblock);
    if (!movables.length) return; /* in-stock: nothing to arbitrate */
    var warningOwnsZone = !!zone.querySelector('.clickable-fit-helpful, .clickable-fit-warnline');
    if (!warningOwnsZone) {
      movables.forEach(function (el) {
        if (el.parentElement === zone) return;
        if (!el.__ckHome) el.__ckHome = { parent: el.parentElement, next: el.nextSibling };
        zone.appendChild(el);
      });
    } else {
      /* reverse order restores siblings against their recorded anchors.
         PERF ROUND item 1: the oosblock now STARTS inside the zone
         (server-rendered) so it has no recorded __ckHome on first
         arbitration — its under-title home is the server-rendered
         [data-oosblock-anchor] marker. */
      movables.slice().reverse().forEach(function (el) {
        if (el.parentElement !== zone) return;
        if (el.__ckHome) { el.__ckHome.parent.insertBefore(el, el.__ckHome.next); return; }
        var anchor = card.querySelector('[data-oosblock-anchor]');
        if (anchor) anchor.parentElement.insertBefore(el, anchor.nextSibling);
      });
    }
  }

  function paintAll() {
    // Brand collections are vehicle-chooser surfaces — never paint chips there.
    if (document.querySelector('[data-suppress-fitment]')) return;
    document.querySelectorAll('[data-product-card]').forEach(function (card) {
      paintCard(card);
      applySlotPriority(card);
    });
  }

  document.addEventListener('clickable:vehicle-changed', function (event) {
    activeName = event.detail.name;
    // Re-vehicling (clickable-revehicle.js): a scoped navigation is imminent
    // and the grid is dimmed -- skip the repaint so the outgoing grid never
    // flashes wrong-vehicle warnings in the beat before the browser leaves.
    // If the navigation never happens, the failsafe clear fires
    // 'clickable:cards-added' to reconcile the cards to the new vehicle.
    if (window.clickableRevehicle && window.clickableRevehicle.isActive()) return;
    paintAll();
  });

  // Late-added cards (the search real-card upgrade wave; any future async
  // surface): dispatch 'clickable:cards-added' after insertion and the
  // full paint pass runs again (idempotent per card).
  document.addEventListener('clickable:cards-added', paintAll);

  // Cart mutations elsewhere (toast adds incl. the PDP path; Dawn drawer
  // quantity/remove changes via pub-sub when its globals exist) re-sync the
  // persistent Added states.
  document.addEventListener('clickable:toast-shown', function () {
    refreshCartState().then(function () {
      document.querySelectorAll('[data-product-card]').forEach(syncAdded);
    });
  });
  // bfcache restore (clickable-revehicle.js): re-fetch the cart and re-sync
  // the persistent Added chips WITHOUT a vehicle-change or toast side effect
  // (a cross-tab cart change on a persisted page).
  document.addEventListener('clickable:cart-revalidate', function () {
    refreshCartState().then(function () {
      document.querySelectorAll('[data-product-card]').forEach(syncAdded);
    });
  });
  /* NAMED TRAP (SESSION-NOTES, struck again 11 Aug): PUB_SUB_EVENTS is a
     top-level const, NOT window.PUB_SUB_EVENTS — the window-guarded form
     was silently false forever, so drawer quantity/remove changes never
     re-synced cards (masked until the live-state warning needed this
     path). Bare-identifier typeof guard, the clamp module's proven
     pattern. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      refreshCartState().then(function () {
        document.querySelectorAll('[data-product-card]').forEach(syncAdded);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    activeName = window.clickableVehicleState ? window.clickableVehicleState.getName() : null;
    // Round-three item 5: the server already painted Added at parse time —
    // seed the cart set from that paint so the first paintAll re-applies
    // it without waiting on (or flickering around) the cart fetch; the
    // fetch then takes over as the live source.
    cartVariants = {};
    document.querySelectorAll('[data-product-card]').forEach(function (card) {
      var btn = card.querySelector('[data-card-atc]');
      if (btn && btn.classList.contains('is-added')) {
        cartVariants[card.getAttribute('data-variant-id')] = true;
      }
      /* chip seed: OOS cards render no ATC — their server-painted chip
         is the only in-cart witness at parse time */
      var chip = card.querySelector('[data-card-added-chip]');
      if (chip && !chip.hasAttribute('hidden')) {
        cartVariants[card.getAttribute('data-variant-id')] = true;
      }
    });
    paintAll();
    refreshCartState().then(function () {
      document.querySelectorAll('[data-product-card]').forEach(syncAdded);
    });

    // Facet filtering re-renders the grid via fetch + innerHTML swap; repaint
    // when new cards arrive. Mutations inside fitment zones are our own
    // painting — ignore them or the observer feeds back on itself.
    var container = document.getElementById('ProductGridContainer');
    if (container && 'MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        var external = mutations.some(function (mutation) {
          var node = mutation.target;
          return !(node.closest && node.closest('[data-fitment-zone], [data-fitment-icon], .clickable-card__atc-zone'));
        });
        if (external) paintAll();
      }).observe(container, { childList: true, subtree: true });
    }
  });
})();
